import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import imageCompression from "browser-image-compression";
import { AlertTriangle, Camera, CalendarClock, Target, ListChecks, FileCheck2, Award } from "lucide-react";
import { api, apiError, fmtDate } from "../lib/api";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter,
} from "./ui/drawer";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { StatusBadge } from "./StatusBadge";
import { localeOf } from "../i18n";

const Row = ({ icon: Icon, label, children, testId }) => (
  <div data-testid={testId}>
    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
      <Icon className="h-3.5 w-3.5" />{label}
    </p>
    <div className="mt-1 whitespace-pre-line text-sm">{children}</div>
  </div>
);

export const MissionDetailSheet = ({ mission, canSubmit, pendingCheckinSync, onClose, onDone }) => {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setNote(mission?.my_submission?.note || ""); setFile(null); }, [mission]);

  const sub = mission?.my_submission;
  const locked = sub?.status === "approved";

  const submit = async () => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append("mission_id", mission.id);
      if (note.trim()) form.append("note", note.trim());
      if (file) {
        const compressed = await imageCompression(file, { maxSizeMB: 1.2, maxWidthOrHeight: 1600 });
        form.append("file", compressed, file.name);
      }
      await api.post("/submissions", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("missions.submitted"));
      setNote(""); setFile(null);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open={!!mission} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DrawerContent data-testid="mission-detail-sheet">
        <div className="mx-auto max-h-[85vh] w-full max-w-[520px] overflow-y-auto">
          <DrawerHeader>
            <DrawerTitle className="font-display text-xl font-bold" data-testid="mission-detail-title">
              {mission?.title}
            </DrawerTitle>
          </DrawerHeader>

          <div className="space-y-4 px-4 pb-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary"
                data-testid="mission-detail-points">
                <Award className="mr-1 inline h-3.5 w-3.5" />{mission?.points} {t("common.points")}
              </span>
              <span className="rounded-full bg-muted px-3 py-1 text-xs font-bold"
                data-testid="mission-detail-type">
                {mission ? t(`missions.${mission.proof_type}`) : ""}
              </span>
              <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
                {mission?.required ? t("missions.required") : t("missions.optionalMission")}
              </span>
              {sub && <StatusBadge status={sub.status} testId="mission-detail-status" />}
              {!sub && <StatusBadge status="new" testId="mission-detail-status" />}
            </div>

            {mission?.description && (
              <Row icon={Target} label={t("missions.objective")} testId="mission-detail-objective">
                {mission.description}
              </Row>
            )}
            {mission?.instructions && (
              <Row icon={ListChecks} label={t("missions.instructions")} testId="mission-detail-instructions">
                {mission.instructions}
              </Row>
            )}
            <Row icon={FileCheck2} label={t("missions.proofExpected")} testId="mission-detail-proof">
              {mission?.proof_expected || (mission ? t(`missions.expected_${mission.proof_type}`) : "")}
            </Row>
            {mission?.due_at && (
              <Row icon={CalendarClock} label={t("missions.deadline")} testId="mission-detail-deadline">
                {fmtDate(mission.due_at, locale)}
              </Row>
            )}

            {sub?.review_reason && (
              <p className="rounded-lg bg-destructive/10 p-3 text-xs text-destructive"
                data-testid="mission-detail-reject-reason">
                {sub.review_reason}
              </p>
            )}

            {!canSubmit && !locked && (
              <p className="rounded-xl bg-muted p-3 text-xs font-semibold text-muted-foreground"
                data-testid="mission-detail-locked">
                {pendingCheckinSync ? t("checkin.pendingSyncNotice") : t("missions.checkinFirst")}
              </p>
            )}
            {canSubmit && !locked && (
              <>
                <p data-testid="proof-content-reminder"
                  className="flex gap-2 rounded-xl bg-[hsl(var(--warning))]/10 p-3 text-xs font-medium text-foreground">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--warning))]" />
                  {t("missions.reminder")}
                </p>

                {mission?.proof_type === "photo" && (
                  <label className="flex h-14 w-full cursor-pointer items-center justify-center gap-2 rounded-full border border-input font-semibold">
                    <Camera className="h-5 w-5" />
                    {file ? file.name.slice(0, 24) : t("missions.takePhoto")}
                    <input data-testid="proof-file-input" type="file" accept="image/*" capture="environment"
                      className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                  </label>
                )}

                {mission?.proof_type !== "completion" && (
                  <div>
                    <label className="text-sm font-semibold">
                      {t("missions.noteLabel")}
                      {mission?.proof_type === "photo" ? ` (${t("common.optional")})` : ""}
                    </label>
                    <Textarea data-testid="proof-note-input" value={note} rows={4}
                      onChange={(e) => setNote(e.target.value)} className="mt-1.5" />
                  </div>
                )}
              </>
            )}
          </div>

          <DrawerFooter>
            {canSubmit && !locked && (
              <Button data-testid="proof-submit-btn" onClick={submit}
                disabled={busy || (mission?.proof_type === "photo" && !file) ||
                  (mission?.proof_type === "note" && !note.trim())}
                className="h-14 w-full rounded-full text-base font-bold">
                {busy ? t("common.loading")
                  : mission?.proof_type === "completion" ? t("missions.completion")
                    : sub ? t("missions.resubmit") : t("missions.submitProof")}
              </Button>
            )}
            <Button data-testid="proof-cancel-btn" variant="outline" onClick={onClose}
              className="h-12 w-full rounded-full font-semibold">{t("common.close")}</Button>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
